// A port of services/domains/experiments/stats.go (Evaluate), so simulated results carry the same
// lifts, intervals, p-values, Holm adjustment and recommendation the backend computes for the same
// counts. Ported from the Ember prototype (customerio/ui#17200, utils/experiments/simulator/stats.ts).

export type SimVariantConfig = { id: string; name: string; role: string; allocation: number };

export type SimVariantResult = {
  variant_id: string;
  exposed: number;
  converted: number;
  rate: number;
  relative_lift: number;
  p_value: number;
  adjusted_p_value: number;
  significant: boolean;
  probability_to_beat_control: number;
  ci_low: number;
  ci_high: number;
};

export type SimComparison = {
  variant_a: string;
  variant_b: string;
  relative_lift: number;
  p_value: number;
  adjusted_p_value: number;
  significant: boolean;
};

export type SimResults = {
  recommendation: string;
  recommended_variant_id: string | null;
  recommended_variant_ids: string[];
  reference_variant_id: string;
  variants: SimVariantResult[];
  comparisons: SimComparison[];
};

export type VariantCounts = Record<string, { exposed: number; converted: number }>;

const MIN_EXPOSURES_PER_VARIANT = 100;
const DEFAULT_Z = 1.6448536;
const Z_FOR_CONFIDENCE = new Map<number, number>([
  [0.9, 1.6448536],
  [0.95, 1.959964],
  [0.99, 2.5758293],
]);

// Abramowitz and Stegun 7.1.26: accurate to ~1.5e-7, plenty for display.
function erf(x: number): number {
  const sign = Math.sign(x);
  const ax = Math.abs(x);
  const t = 1 / (1 + 0.3275911 * ax);
  const poly = ((((1.061405429 * t - 1.453152027) * t + 1.421413741) * t - 0.284496736) * t + 0.254829592) * t;
  return sign * (1 - poly * Math.exp(-ax * ax));
}

export function normalCDF(z: number): number {
  return 0.5 * (1 + erf(z / Math.SQRT2));
}

function zForConfidence(level: number): number {
  return Z_FOR_CONFIDENCE.get(level) ?? DEFAULT_Z;
}

function twoTailedPValue(z: number): number {
  return 2 * (1 - normalCDF(Math.abs(z)));
}

function relativeLift(controlRate: number, rate: number): number {
  return controlRate === 0 ? 0 : (rate - controlRate) / controlRate;
}

type Scored = { id: string; exposed: number; converted: number; rate: number };

function hasNoExposures(a: Scored, b: Scored): boolean {
  return a.exposed === 0 || b.exposed === 0;
}

function twoProportionZ(control: Scored, treatment: Scored): number {
  if (hasNoExposures(control, treatment)) {
    return 0;
  }
  const pooled = (control.converted + treatment.converted) / (control.exposed + treatment.exposed);
  const variance = pooled * (1 - pooled) * (1 / control.exposed + 1 / treatment.exposed);
  return variance <= 0 ? 0 : (treatment.rate - control.rate) / Math.sqrt(variance);
}

function confidenceInterval(control: Scored, treatment: Scored, level: number): [number, number] {
  if (hasNoExposures(control, treatment) || control.rate === 0) {
    return [0, 0];
  }
  const se = Math.sqrt(
    (control.rate * (1 - control.rate)) / control.exposed + (treatment.rate * (1 - treatment.rate)) / treatment.exposed,
  );
  const z = zForConfidence(level);
  const diff = treatment.rate - control.rate;
  return [(diff - z * se) / control.rate, (diff + z * se) / control.rate];
}

function compareIds(a: Scored, b: Scored): number {
  if (a.id === b.id) {
    return 0;
  }
  return a.id < b.id ? -1 : 1;
}

function scoreVariants(variants: SimVariantConfig[], counts: VariantCounts): Scored[] {
  return variants
    .map((variant) => {
      const count = counts[variant.id] ?? { exposed: 0, converted: 0 };
      const rate = count.exposed === 0 ? 0 : count.converted / count.exposed;
      return { id: variant.id, exposed: count.exposed, converted: count.converted, rate };
    })
    .sort(compareIds);
}

function blankResult(scored: Scored): SimVariantResult {
  return {
    variant_id: scored.id,
    exposed: scored.exposed,
    converted: scored.converted,
    rate: scored.rate,
    relative_lift: 0,
    p_value: 0,
    adjusted_p_value: 0,
    significant: false,
    probability_to_beat_control: 0,
    ci_low: 0,
    ci_high: 0,
  };
}

function againstReference(reference: Scored, scored: Scored, level: number): Partial<SimVariantResult> {
  const z = twoProportionZ(reference, scored);
  const [low, high] = confidenceInterval(reference, scored, level);
  return {
    relative_lift: relativeLift(reference.rate, scored.rate),
    p_value: twoTailedPValue(z),
    probability_to_beat_control: normalCDF(z),
    ci_low: low,
    ci_high: high,
  };
}

/** Indexes of the variants in the running: the reference plus every variant still allocated traffic. */
function contendersOf(scored: Scored[], variants: SimVariantConfig[], referenceIndex: number): number[] {
  const allocation = new Map(variants.map((variant) => [variant.id, variant.allocation]));
  return scored
    .map((_, index) => index)
    .filter((index) => index === referenceIndex || (allocation.get(scored[index].id) ?? 1) > 0);
}

function hasEnoughData(contenders: number[], scored: Scored[]): boolean {
  return contenders.length >= 2 && contenders.every((index) => scored[index].exposed >= MIN_EXPOSURES_PER_VARIANT);
}

type Pairwise = { comparisons: SimComparison[]; pair: (a: number, b: number) => SimComparison };

function pairKey(a: number, b: number): string {
  return a < b ? `${a}:${b}` : `${b}:${a}`;
}

function compare(scored: Scored[], a: number, b: number): SimComparison {
  return {
    variant_a: scored[a].id,
    variant_b: scored[b].id,
    relative_lift: relativeLift(scored[a].rate, scored[b].rate),
    p_value: twoTailedPValue(twoProportionZ(scored[a], scored[b])),
    adjusted_p_value: 0,
    significant: false,
  };
}

// Holm: scale each rank by the comparisons still untested; the running max keeps it monotone.
function holmAdjust(comparisons: SimComparison[], alpha: number): void {
  const ranked = comparisons.map((_, index) => index).sort((x, y) => comparisons[x].p_value - comparisons[y].p_value);
  let running = 0;
  ranked.forEach((index, rank) => {
    running = Math.max(running, Math.min(1, (ranked.length - rank) * comparisons[index].p_value));
    comparisons[index].adjusted_p_value = running;
    comparisons[index].significant = running < alpha;
  });
}

function pairwise(contenders: number[], scored: Scored[], alpha: number): Pairwise {
  const comparisons: SimComparison[] = [];
  const position = new Map<string, number>();
  contenders.forEach((a, index) => {
    for (const b of contenders.slice(index + 1)) {
      position.set(pairKey(a, b), comparisons.length);
      comparisons.push(compare(scored, a, b));
    }
  });
  holmAdjust(comparisons, alpha);
  return { comparisons, pair: (a, b) => comparisons[position.get(pairKey(a, b)) ?? 0] };
}

type Standing = {
  anySignificant: boolean;
  referenceBeatsAll: boolean;
  best: number;
  beatsEveryone: boolean;
  leading: string[];
};

function referenceStanding(input: EvaluationState, out: SimVariantResult[]) {
  const { contenders, referenceIndex, scored, pairs } = input;
  let anySignificant = false;
  let referenceBeatsAll = true;
  for (const index of contenders.filter((candidate) => candidate !== referenceIndex)) {
    const comparison = pairs.pair(referenceIndex, index);
    out[index].adjusted_p_value = comparison.adjusted_p_value;
    out[index].significant = comparison.significant;
    anySignificant ||= comparison.significant;
    referenceBeatsAll &&= comparison.significant && scored[index].rate < scored[referenceIndex].rate;
  }
  return { anySignificant, referenceBeatsAll };
}

function bestContender(contenders: number[], scored: Scored[]): number {
  return contenders.reduce((best, index) => (scored[index].rate > scored[best].rate ? index : best), contenders[0]);
}

function leadingVariants({ contenders, scored, pairs }: EvaluationState, best: number): string[] {
  const tied = contenders.filter((index) => index !== best && !pairs.pair(best, index).significant);
  return [scored[best].id, ...tied.map((index) => scored[index].id)];
}

type EvaluationState = {
  scored: Scored[];
  contenders: number[];
  referenceIndex: number;
  pairs: Pairwise;
  enrollmentEnded: boolean;
};

type Recommendation = Pick<SimResults, 'recommendation' | 'recommended_variant_id' | 'recommended_variant_ids'>;

function recommendOnly(recommendation: string, ids: string[] = []): Recommendation {
  return { recommendation, recommended_variant_id: null, recommended_variant_ids: ids };
}

function recommendVariant(recommendation: string, id: string): Recommendation {
  return { recommendation, recommended_variant_id: id, recommended_variant_ids: [id] };
}

type Rule = (state: EvaluationState, standing: Standing) => Recommendation | null;

// Checked in order, the same order as stats.go; the first rule that answers wins.
const RULES: Rule[] = [
  ({ referenceIndex, scored }, { beatsEveryone, best }) =>
    beatsEveryone && best === referenceIndex
      ? recommendVariant('control_recommended', scored[referenceIndex].id)
      : null,
  ({ referenceIndex, scored }, { beatsEveryone, best }) =>
    beatsEveryone && scored[best].rate > scored[referenceIndex].rate
      ? recommendVariant('winner', scored[best].id)
      : null,
  ({ contenders }, { leading }) =>
    leading.length > 1 && leading.length < contenders.length ? recommendOnly('leading_variants', leading) : null,
  ({ referenceIndex, scored }, { referenceBeatsAll }) =>
    referenceBeatsAll ? recommendVariant('control_recommended', scored[referenceIndex].id) : null,
  ({ enrollmentEnded }, { anySignificant }) =>
    !anySignificant && enrollmentEnded ? recommendOnly('no_meaningful_difference') : null,
];

function recommend(state: EvaluationState, standing: Standing): Recommendation {
  for (const rule of RULES) {
    const answer = rule(state, standing);
    if (answer != null) {
      return answer;
    }
  }
  return recommendOnly('inconclusive');
}

export type EvaluateInput = {
  variants: SimVariantConfig[];
  counts: VariantCounts;
  confidenceLevel: number;
  enrollmentEnded: boolean;
};

function standingOf(state: EvaluationState, out: SimVariantResult[]): Standing {
  const best = bestContender(state.contenders, state.scored);
  const leading = leadingVariants(state, best);
  return { ...referenceStanding(state, out), best, leading, beatsEveryone: leading.length === 1 };
}

export function evaluate({ variants, counts, confidenceLevel, enrollmentEnded }: EvaluateInput): SimResults {
  const referenceId = variants[0]?.id ?? '';
  const scored = scoreVariants(variants, counts);
  const out = scored.map(blankResult);
  const insufficient: SimResults = {
    ...recommendOnly('insufficient_data'),
    reference_variant_id: referenceId,
    variants: out,
    comparisons: [],
  };
  const referenceIndex = scored.findIndex((item) => item.id === referenceId);
  if (referenceIndex < 0) {
    return insufficient;
  }
  scored.forEach((item, index) => {
    if (index !== referenceIndex) {
      Object.assign(out[index], againstReference(scored[referenceIndex], item, confidenceLevel));
    }
  });
  return withComparisons({ variants, scored, referenceIndex, confidenceLevel, enrollmentEnded }, insufficient);
}

type ComparisonInput = {
  variants: SimVariantConfig[];
  scored: Scored[];
  referenceIndex: number;
  confidenceLevel: number;
  enrollmentEnded: boolean;
};

function withComparisons(input: ComparisonInput, insufficient: SimResults): SimResults {
  const contenders = contendersOf(input.scored, input.variants, input.referenceIndex);
  if (!hasEnoughData(contenders, input.scored)) {
    return insufficient;
  }
  const pairs = pairwise(contenders, input.scored, 1 - input.confidenceLevel);
  const state: EvaluationState = { ...input, contenders, pairs };
  const standing = standingOf(state, insufficient.variants);
  return { ...insufficient, ...recommend(state, standing), comparisons: pairs.comparisons };
}
